import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { groupBookingEndpoints } from '../api-group-bookings';
import type { GroupProduct, GroupProductInput, Lookups, Settings } from '../api-group-bookings';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel, Table } from '../ui';
import { INPUT, dollars } from './event/shared';

/**
 * Spaces and packages (D-376): what a group can book, what each includes and
 * costs, a game's own price (fireworks nights), and the group sales settings —
 * where inquiries go, who gets them, how long a hold lasts.
 */

const EMPTY: GroupProductInput = {
  code: '',
  name: '',
  kind: 'SPACE',
  venue_id: null,
  summary: null,
  description: null,
  capacity: null,
  min_guests: null,
  price_cents: 0,
  per_game_limit: 1,
  included_tickets: 0,
  ticket_type_code: 'ADULT',
  ticket_zone_code: 'GA',
  whole_section_code: null,
  ticket_price_cents: null,
  map_section_code: null,
  public_listed: true,
  active: true,
  sort_order: 100,
  inclusions: [],
  options: [],
};

const toInput = (p: GroupProduct): GroupProductInput => ({
  code: p.code,
  name: p.name,
  kind: p.kind,
  venue_id: p.venue_id,
  summary: p.summary,
  description: p.description,
  capacity: p.capacity,
  min_guests: p.min_guests,
  price_cents: p.price_cents,
  per_game_limit: p.per_game_limit,
  included_tickets: p.included_tickets,
  ticket_type_code: p.ticket_type_code,
  ticket_zone_code: p.ticket_zone_code,
  whole_section_code: p.whole_section_code,
  ticket_price_cents: p.ticket_price_cents,
  map_section_code: p.map_section_code,
  public_listed: p.public_listed === 1,
  active: p.active === 1,
  sort_order: p.sort_order,
  inclusions: p.inclusions.map((i) => ({ quantity: i.quantity, label: i.label })),
  options: p.options.map((o) => ({ option_product_id: o.option_product_id, price_cents: o.price_cents })),
});

const cents = (value: string): number | null => (value.trim() === '' ? null : Math.round(Number(value.replace(/[^0-9.]/g, '')) * 100));
const money = (value: number | null) => (value === null ? '' : String(value / 100));
const int = (value: string): number | null => (value.trim() === '' ? null : Number(value.replace(/\D/g, '')));

export default function GroupSpacesPage() {
  const can = usePermissions();
  const [editing, setEditing] = useState<{ id: string | null; input: GroupProductInput } | null>(null);
  const [pricing, setPricing] = useState<GroupProduct | null>(null);
  const products = useQuery({ queryKey: ['group-products'], queryFn: groupBookingEndpoints.products });
  if (!can('group_sales.view')) return <NotPermitted permission="group_sales.view" />;
  const manage = can('group_sales.manage');
  const list = products.data?.products ?? [];

  return (
    <div className="space-y-4">
      <Panel
        title="Spaces and packages"
        description="What a group can book. A space is booked whole for a game; a package is a bundle; a ticket block is tickets at a group rate."
        actions={manage ? <Button onClick={() => setEditing({ id: null, input: EMPTY })}>Add one</Button> : null}
      >
        <Table headers={['Name', 'Kind', 'Price', 'Includes', 'Per game', 'Shown', '']}>
          {list.map((p) => (
            <tr key={p.id} className={`border-b border-line last:border-0 ${p.active ? '' : 'text-ink-400'}`}>
              <td className="px-5 py-3 text-sm">
                <span className="font-medium">{p.name}</span>
                <div className="font-mono text-xs text-ink-500">{p.code}</div>
              </td>
              <td className="px-5 py-3 text-sm">{p.kind.toLowerCase().replace('_', ' ')}</td>
              <td className="tabular px-5 py-3 text-sm">
                {p.price_cents !== null ? dollars(p.price_cents) : '—'}
                {p.ticket_price_cents !== null ? <div className="text-xs text-ink-500">{dollars(p.ticket_price_cents)} a ticket</div> : null}
                {p.options.map((o) => (
                  <div key={o.option_product_id} className="text-xs text-ink-500">
                    + {o.name} {dollars(o.price_cents)}
                  </div>
                ))}
              </td>
              <td className="px-5 py-3 text-sm">
                {[p.included_tickets ? `${p.included_tickets} tickets` : null, p.whole_section_code ? `every ${p.whole_section_code} seat` : null, ...p.inclusions.map((i) => (i.quantity ? `${i.quantity} ${i.label}` : i.label))].filter(Boolean).join(', ') || '—'}
              </td>
              <td className="px-5 py-3 text-sm">{p.per_game_limit ?? 'Unlimited'}</td>
              <td className="px-5 py-3 text-sm">{p.public_listed ? <Badge tone="ok">website</Badge> : <Badge>staff only</Badge>}</td>
              <td className="px-5 py-3 text-right text-sm">
                {manage ? (
                  <div className="flex justify-end gap-2">
                    <Button onClick={() => setEditing({ id: p.id, input: toInput(p) })}>Edit</Button>
                    {p.kind !== 'TICKET_BLOCK' ? <Button onClick={() => setPricing(p)}>Game prices</Button> : null}
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      </Panel>
      {editing ? <ProductForm key={editing.id ?? 'new'} id={editing.id} initial={editing.input} all={list} onClose={() => setEditing(null)} /> : null}
      {pricing ? <GamePrices key={pricing.id} product={pricing} onClose={() => setPricing(null)} /> : null}
      <SettingsPanel canEdit={manage} />
    </div>
  );
}

function ProductForm({ id, initial, all, onClose }: { id: string | null; initial: GroupProductInput; all: GroupProduct[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [input, setInput] = useState(initial);
  const [inclusions, setInclusions] = useState(initial.inclusions.map((i) => (i.quantity ? `${i.quantity} ${i.label}` : i.label)).join('\n'));
  const save = useMutation({
    mutationFn: () => {
      const parsed = inclusions
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const match = line.match(/^(\d+)\s+(.+)$/);
          return match ? { quantity: Number(match[1]), label: match[2]! } : { quantity: null, label: line };
        });
      const body = { ...input, inclusions: parsed };
      return id ? groupBookingEndpoints.updateProduct(id, body) : groupBookingEndpoints.createProduct(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group-products'] });
      onClose();
    },
  });
  const set = <K extends keyof GroupProductInput>(key: K, value: GroupProductInput[K]) => setInput({ ...input, [key]: value });
  return (
    <Panel title={id ? `Edit ${initial.name}` : 'A new space or package'}>
      <form
        className="grid gap-3 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.error ? <div className="sm:col-span-2 lg:col-span-4"><ErrorNote>{(save.error as ApiError).message}</ErrorNote></div> : null}
        <Field label="Code" htmlFor="gp-code">
          <input id="gp-code" required disabled={Boolean(id)} value={input.code} onChange={(e) => set('code', e.target.value.toUpperCase())} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Name" htmlFor="gp-name">
          <input id="gp-name" required value={input.name} onChange={(e) => set('name', e.target.value)} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Kind" htmlFor="gp-kind">
          <select id="gp-kind" value={input.kind} onChange={(e) => setInput({ ...input, kind: e.target.value as GroupProductInput['kind'], ...(e.target.value === 'TICKET_BLOCK' ? { price_cents: null, per_game_limit: null } : {}) })} className={`w-full ${INPUT}`}>
            <option value="SPACE">Space (booked whole)</option>
            <option value="PACKAGE">Package</option>
            <option value="TICKET_BLOCK">Ticket block</option>
          </select>
        </Field>
        <Field label="Bookings per game" htmlFor="gp-limit" hint="1 for a room; blank is unlimited">
          <input id="gp-limit" value={input.per_game_limit ?? ''} onChange={(e) => set('per_game_limit', int(e.target.value))} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Price ($)" htmlFor="gp-price">
          <input id="gp-price" disabled={input.kind === 'TICKET_BLOCK'} value={money(input.price_cents)} onChange={(e) => set('price_cents', cents(e.target.value))} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Holds (people)" htmlFor="gp-capacity">
          <input id="gp-capacity" value={input.capacity ?? ''} onChange={(e) => set('capacity', int(e.target.value))} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Group minimum" htmlFor="gp-min">
          <input id="gp-min" value={input.min_guests ?? ''} onChange={(e) => set('min_guests', int(e.target.value))} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Included tickets" htmlFor="gp-included">
          <input id="gp-included" value={input.included_tickets || ''} onChange={(e) => set('included_tickets', int(e.target.value) ?? 0)} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Ticket type code" htmlFor="gp-type">
          <input id="gp-type" value={input.ticket_type_code ?? ''} onChange={(e) => set('ticket_type_code', e.target.value.toUpperCase() || null)} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Price zone code" htmlFor="gp-zone" hint="Where the tickets come from">
          <input id="gp-zone" value={input.ticket_zone_code ?? ''} onChange={(e) => set('ticket_zone_code', e.target.value.toUpperCase() || null)} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Whole section code" htmlFor="gp-section" hint="Every seat in it goes with the booking">
          <input id="gp-section" value={input.whole_section_code ?? ''} onChange={(e) => set('whole_section_code', e.target.value.toUpperCase() || null)} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Group rate a ticket ($)" htmlFor="gp-rate" hint="Blank is the game's price">
          <input id="gp-rate" value={money(input.ticket_price_cents)} onChange={(e) => set('ticket_price_cents', cents(e.target.value))} className={`w-full ${INPUT}`} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Where and what it is" htmlFor="gp-summary">
            <input id="gp-summary" value={input.summary ?? ''} onChange={(e) => set('summary', e.target.value || null)} className={`w-full ${INPUT}`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Also included, one a line" htmlFor="gp-incl" hint="“25 Chicken Lips”, “a Bees birthday cake”">
            <textarea id="gp-incl" rows={3} value={inclusions} onChange={(e) => setInclusions(e.target.value)} className={`w-full ${INPUT}`} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Can be added" htmlFor="gp-options">
            <div id="gp-options" className="space-y-1">
              {all
                .filter((p) => p.id !== id && p.kind === 'SPACE')
                .map((p) => {
                  const chosen = input.options.find((o) => o.option_product_id === p.id);
                  return (
                    <div key={p.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={Boolean(chosen)} onChange={(e) => set('options', e.target.checked ? [...input.options, { option_product_id: p.id, price_cents: 0 }] : input.options.filter((o) => o.option_product_id !== p.id))} aria-label={`Offer ${p.name}`} />
                      <span className="w-56">{p.name}</span>
                      {chosen ? <input value={money(chosen.price_cents)} onChange={(e) => set('options', input.options.map((o) => (o.option_product_id === p.id ? { ...o, price_cents: cents(e.target.value) ?? 0 } : o)))} aria-label={`${p.name} price`} className={`w-24 ${INPUT}`} /> : null}
                    </div>
                  );
                })}
            </div>
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm sm:col-span-2 lg:col-span-4">
          <label className="flex items-center gap-1"><input type="checkbox" checked={input.public_listed} onChange={(e) => set('public_listed', e.target.checked)} /> On the website</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={input.active} onChange={(e) => set('active', e.target.checked)} /> Bookable</label>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Panel>
  );
}

/** A game's own price: fireworks nights, a holiday, a sponsor's night. */
function GamePrices({ product, onClose }: { product: GroupProduct; onClose: () => void }) {
  const detail = useQuery({ queryKey: ['group-product', product.id], queryFn: () => groupBookingEndpoints.product(product.id) });
  const board = useQuery({ queryKey: ['group-board', 'upcoming'], queryFn: () => groupBookingEndpoints.board() });
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (detail.data) setValues(Object.fromEntries(detail.data.game_prices.map((p) => [p.performance_id, String(p.price_cents / 100)])));
  }, [detail.data]);
  const save = useMutation({
    mutationFn: () => groupBookingEndpoints.gamePrices(product.id, (board.data?.games ?? []).map((g) => ({ performance_id: g.performance_id, price_cents: values[g.performance_id]?.trim() ? cents(values[g.performance_id]!) : null }))),
    onSuccess: () => onClose(),
  });
  return (
    <Panel title={`${product.name}: each game's price`} description={`Blank is the usual ${dollars(product.price_cents)}.`}>
      {save.error ? <ErrorNote>{(save.error as ApiError).message}</ErrorNote> : null}
      <div className="grid gap-2 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3">
        {(board.data?.games ?? []).map((g) => (
          <label key={g.performance_id} className="flex items-center justify-between gap-2 text-sm">
            <span>
              {g.local_event_date} · {g.event_title}
            </span>
            <input value={values[g.performance_id] ?? ''} onChange={(e) => setValues({ ...values, [g.performance_id]: e.target.value })} placeholder={money(product.price_cents)} className={`w-24 ${INPUT}`} />
          </label>
        ))}
      </div>
      <div className="flex gap-2 border-t border-line px-5 py-3">
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save prices
        </Button>
        <Button onClick={onClose}>Close</Button>
      </div>
    </Panel>
  );
}

function SettingsPanel({ canEdit }: { canEdit: boolean }) {
  const settings = useQuery({ queryKey: ['group-settings'], queryFn: groupBookingEndpoints.settings });
  const lookups = useQuery<Lookups, ApiError>({ queryKey: ['group-lookups'], queryFn: groupBookingEndpoints.lookups });
  const [form, setForm] = useState<Settings | null>(null);
  useEffect(() => {
    if (settings.data) setForm(settings.data.settings);
  }, [settings.data]);
  const save = useMutation({ mutationFn: () => groupBookingEndpoints.saveSettings(form!) });
  if (!form) return null;
  return (
    <Panel title="Settings" description="Where a website inquiry is announced, who it goes to, and how long a hold lasts when nobody says.">
      <div className="flex flex-wrap items-end gap-3 px-5 py-4">
        {save.error ? <ErrorNote>{(save.error as ApiError).message}</ErrorNote> : null}
        <Field label="Group sales inbox" htmlFor="gs-inbox" hint="Blank is the contact email in Branding">
          <input id="gs-inbox" type="email" disabled={!canEdit} value={form.inbox_email ?? ''} onChange={(e) => setForm({ ...form, inbox_email: e.target.value.trim() || null })} className={`w-72 ${INPUT}`} />
        </Field>
        <Field label="New inquiries go to" htmlFor="gs-owner">
          <select id="gs-owner" disabled={!canEdit} value={form.default_owner_staff_account_id ?? ''} onChange={(e) => setForm({ ...form, default_owner_staff_account_id: e.target.value || null })} className={INPUT}>
            <option value="">Nobody (pick up from the list)</option>
            {(lookups.data?.owners ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.display_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="A hold lasts (days)" htmlFor="gs-days">
          <input id="gs-days" disabled={!canEdit} value={form.hold_days} onChange={(e) => setForm({ ...form, hold_days: Number(e.target.value.replace(/\D/g, '') || 1) })} className={`w-20 ${INPUT}`} />
        </Field>
        {canEdit ? (
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isSuccess ? 'Saved' : 'Save'}
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}
